import React from 'react';
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  View,
} from 'react-native';
import {useTranslation} from 'react-i18next';
import Toast from 'react-native-toast-message';
import AuthHeader from '../../components/AuthHeader';
import {BASE_API} from '../../config/apis';
import {APP_VERSION} from '../../config/appHeaders';
import {
  DEV_FLAGS,
  resetDevFlags,
  setDevFlag,
  setDevUnlocked,
  useDevSettings,
} from '../../config/devSettings';
import {colors, card, radii, spacing, typography} from '../../config/theme';

const FlagRow = ({title, description, value, onChange, isLast}) => (
  <>
    <View style={styles.row}>
      <View style={styles.rowText}>
        <Text style={styles.rowTitle}>{title}</Text>
        <Text style={styles.rowSubtitle}>{description}</Text>
      </View>
      <Switch
        value={!!value}
        onValueChange={onChange}
        trackColor={{false: colors.border, true: colors.primary}}
        thumbColor={Platform.OS === 'android' ? colors.surface : undefined}
        ios_backgroundColor={colors.border}
      />
    </View>
    {!isLast && <View style={styles.divider} />}
  </>
);

const InfoRow = ({label, value, isLast}) => (
  <>
    <View style={styles.infoRow}>
      <Text style={styles.infoLabel}>{label}</Text>
      <Text style={styles.infoValue} selectable numberOfLines={2}>
        {value}
      </Text>
    </View>
    {!isLast && <View style={styles.divider} />}
  </>
);

const DevSettings = ({navigation}) => {
  const {t} = useTranslation();
  const settings = useDevSettings();
  const flags = settings?.flags || {};

  const hide = async () => {
    await setDevUnlocked(false);
    Toast.show({type: 'info', text1: t('root.DevSettings.hidden')});
    navigation.goBack();
  };

  return (
    <View style={styles.screen}>
      <AuthHeader
        title={t('root.DevSettings.title')}
        onBack={() => navigation.goBack()}
      />
      <ScrollView
        contentContainerStyle={styles.content}
        showsVerticalScrollIndicator={false}>
        <Text style={styles.note}>{t('root.DevSettings.note')}</Text>

        <Text style={styles.sectionLabel}>
          {t('root.DevSettings.sectionScans')}
        </Text>
        <View style={styles.card}>
          {DEV_FLAGS.map((flag, i) => (
            <FlagRow
              key={flag.key}
              title={t(`root.DevSettings.flags.${flag.key}.title`)}
              description={t(`root.DevSettings.flags.${flag.key}.description`)}
              value={flags[flag.key]}
              onChange={v => setDevFlag(flag.key, v)}
              isLast={i === DEV_FLAGS.length - 1}
            />
          ))}
        </View>

        <Text style={styles.sectionLabel}>
          {t('root.DevSettings.sectionInfo')}
        </Text>
        <View style={styles.card}>
          <InfoRow label={t('root.DevSettings.server')} value={BASE_API} />
          <InfoRow label={t('root.DevSettings.appVersion')} value={APP_VERSION} />
          <InfoRow
            label={t('root.DevSettings.platform')}
            value={`${Platform.OS} ${Platform.Version ?? ''}`.trim()}
            isLast
          />
        </View>

        <Pressable
          onPress={resetDevFlags}
          accessibilityRole="button"
          style={({pressed}) => [styles.button, pressed && styles.pressed]}>
          <Text style={styles.buttonText}>{t('root.DevSettings.reset')}</Text>
        </Pressable>
        <Pressable
          onPress={hide}
          accessibilityRole="button"
          style={({pressed}) => [styles.button, pressed && styles.pressed]}>
          <Text style={[styles.buttonText, styles.hideText]}>
            {t('root.DevSettings.hide')}
          </Text>
        </Pressable>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  screen: {flex: 1, backgroundColor: colors.background},
  content: {
    paddingHorizontal: spacing.margin,
    paddingTop: spacing.md,
    paddingBottom: spacing.xxl,
    gap: spacing.sm,
  },
  note: {
    ...typography.bodySm,
    color: colors.onWarningContainer,
    backgroundColor: colors.warningContainer,
    borderRadius: radii.sm,
    padding: spacing.md,
  },
  sectionLabel: {
    ...typography.labelSm,
    color: colors.textMuted,
    marginTop: spacing.md,
    marginLeft: spacing.xs,
  },
  card: {...card, paddingHorizontal: spacing.md},
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingVertical: spacing.md,
  },
  rowText: {flex: 1, gap: 2},
  rowTitle: {...typography.labelLg, letterSpacing: 0, color: colors.text},
  rowSubtitle: {...typography.bodySm, color: colors.textMuted},
  divider: {height: 1, backgroundColor: colors.borderSubtle},
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: spacing.md,
    paddingVertical: spacing.md,
  },
  infoLabel: {...typography.bodyMd, color: colors.textMuted},
  infoValue: {
    ...typography.labelMd,
    color: colors.text,
    flexShrink: 1,
    textAlign: 'right',
  },
  button: {
    ...card,
    alignItems: 'center',
    paddingVertical: spacing.md,
    marginTop: spacing.sm,
  },
  pressed: {opacity: 0.7},
  buttonText: {...typography.labelLg, letterSpacing: 0, color: colors.primary},
  hideText: {color: colors.error},
});

export default DevSettings;
